/* eslint-disable no-use-before-define */

const URL_INDEX_REGISTRE_FLUX = '/registre-flux/index.json';
const URL_GOLDEN_CASES = '/registre-flux/golden-cases.json';
const FICHIERS_REGISTRE_FALLBACK = [
  'flux-capture-raw.json',
  'profil-interpretation-capture-raw.json',
];
const DUREE_CACHE_MS = 30 * 1000;
const CLE_STOCKAGE_GOLDEN_CASES = 'laboratoire_interpretation_golden_cases.v1';

let cacheLaboratoire = null;
let cacheExpireA = 0;
let chargementEnCours = null;

export const chargerRegistreInterpretation = async () => {
  const maintenant = Date.now();
  if (cacheLaboratoire && maintenant < cacheExpireA) return cacheLaboratoire;
  if (chargementEnCours) return chargementEnCours;

  chargementEnCours = chargerIndexRegistre()
    .then((fichiers) => Promise.all(fichiers.map(chargerObjetRegistre)))
    .then((objets) => {
      const registre = construireRegistre(objets.filter(Boolean));
      cacheLaboratoire = registre;
      cacheExpireA = Date.now() + DUREE_CACHE_MS;
      chargementEnCours = null;
      return registre;
    })
    .catch((erreur) => {
      chargementEnCours = null;
      throw erreur;
    });

  return chargementEnCours;
};

export const detecterPreuvesDepuisLog = (profil, extraitLog) => {
  const texte = String(extraitLog || '');
  const preuvesAttendues = profil && profil.preuves_attendues ? profil.preuves_attendues : [];
  const preuvesContradictoires = profil && profil.preuves_contradictoires ? profil.preuves_contradictoires : [];
  const detecteesAttendues = preuvesAttendues.filter((preuve) => preuveDetectee(preuve, texte));
  const detecteesContradictoires = preuvesContradictoires.filter((preuve) => preuveDetectee(preuve, texte));
  const idsDetectes = new Set(detecteesAttendues.map((preuve) => preuve.id));
  detecteesContradictoires.forEach((preuve) => idsDetectes.add(preuve.id));
  const absentes = preuvesAttendues.filter((preuve) => !idsDetectes.has(preuve.id));
  const reglesDeclenchees = reglesDeclencheesDepuisProfil(profil, idsDetectes);
  const contradictions = calculerContradictions(profil, idsDetectes, detecteesAttendues, detecteesContradictoires);
  const jalonsAtteints = jalonsConcernes(detecteesAttendues);
  const jalonsNonAtteints = jalonsConcernes(absentes);
  const diagnostic = diagnostiquerFluxLocal({
    profil,
    detecteesAttendues,
    detecteesContradictoires,
    absentes,
    contradictions,
    reglesDeclenchees,
    jalonsAtteints,
    jalonsNonAtteints,
  });

  return {
    detectees: detecteesAttendues.concat(detecteesContradictoires),
    absentes,
    contradictoires: detecteesContradictoires,
    contradictions,
    jalons: jalonsConcernes(detecteesAttendues.concat(absentes)),
    jalons_atteints: jalonsAtteints,
    jalons_non_atteints: jalonsNonAtteints,
    regles_declenchees: reglesDeclenchees,
    diagnostic,
    connaissance: {
      type_objet: 'connaissance_interpretation_flux',
      version_schema: 'connaissance-interpretation-flux.local.v1',
      profil_id: profil && profil.identite && profil.identite.id,
      flux_id: profil && profil.identite && profil.identite.flux_id,
      preuves_positives: detecteesAttendues,
      preuves_contradictoires: detecteesContradictoires,
      preuves_manquantes: absentes,
      contradictions,
      jalons_atteints: jalonsAtteints,
      jalons_non_atteints: jalonsNonAtteints,
      regles_declenchees: reglesDeclenchees,
      diagnostic,
    },
  };
};

export const chargerBibliothequeGoldenCases = async () => {
  const embarques = await chargerGoldenCasesEmbarques();
  const locaux = lireGoldenCasesLocaux();
  return fusionnerGoldenCases(embarques, locaux);
};

export const creerGoldenCaseDepuisSimulation = ({
  nom,
  description,
  flux,
  profil,
  extraitLog,
  referenceLog,
  resultat,
  remarques,
}) => {
  const maintenant = new Date().toISOString();
  const fluxId = idFlux(flux) || (profil && profil.identite && profil.identite.flux_id);
  return {
    type_objet: 'golden_case_interpretation_flux',
    version_schema: 'golden-case-interpretation-flux.schema.v1',
    identite: {
      id: 'golden-case:' + slugifier(nom || fluxId || 'cas') + ':' + Date.now(),
      nom: nom || 'Golden Case sans nom',
      description: description || '',
      flux_id: fluxId,
      profil_id: profil && profil.identite && profil.identite.id,
      version: 'v1.0',
      cree_le: maintenant,
      modifie_le: maintenant,
      origine: 'laboratoire',
    },
    entree: {
      type_entree: 'extrait_log',
      reference_log: referenceLog || null,
      extrait_log: extraitLog || '',
    },
    moteur: {
      passe: 'premiere_passe_deterministe',
      version_moteur: 'local.v1',
    },
    attendu: resultatAttenduDepuisResultat(resultat),
    validation: {
      derniere_execution: null,
      dernier_statut: 'jamais_execute',
      dernier_resume: null,
    },
    remarques: remarques || '',
  };
};

export const sauvegarderGoldenCaseLocal = (goldenCase) => {
  const existants = lireGoldenCasesLocaux();
  const prochain = fusionnerGoldenCases(existants.filter((item) => idGoldenCase(item) !== idGoldenCase(goldenCase)), [goldenCase]);
  ecrireGoldenCasesLocaux(prochain);
  return prochain;
};

export const sauvegarderBibliothequeGoldenCasesLocale = (goldenCases) => {
  const prochain = fusionnerGoldenCases(goldenCases || []);
  ecrireGoldenCasesLocaux(prochain);
  return prochain;
};

export const executerGoldenCase = (goldenCase, registre) => {
  const fluxId = goldenCase && goldenCase.identite && goldenCase.identite.flux_id;
  const profil = registre && registre.profilsParFlux ? registre.profilsParFlux[fluxId] : null;
  const resultat = detecterPreuvesDepuisLog(profil, goldenCase && goldenCase.entree && goldenCase.entree.extrait_log);
  const comparaison = comparerResultatGoldenCase(goldenCase, resultat);
  return {
    goldenCase,
    resultat,
    comparaison,
  };
};

export const executerGoldenCases = (goldenCases, registre) => {
  const executions = (goldenCases || []).map((goldenCase) => executerGoldenCase(goldenCase, registre));
  return {
    total: executions.length,
    conformes: executions.filter((execution) => execution.comparaison.conforme).length,
    regressions: executions.filter((execution) => !execution.comparaison.conforme).length,
    executions,
  };
};

export const comparerResultatGoldenCase = (goldenCase, resultat) => {
  const attendu = (goldenCase && goldenCase.attendu) || {};
  const obtenu = resultatAttenduDepuisResultat(resultat);
  const differences = [];

  ajouterDifference(differences, 'mission', attendu.mission, obtenu.mission);
  ajouterDifference(differences, 'etat_flux', attendu.etat_flux, obtenu.etat_flux);
  ajouterDifference(differences, 'raison_principale', attendu.raison_principale, obtenu.raison_principale);
  ajouterDifferenceTableau(differences, 'preuves_detectees', attendu.preuves_detectees, obtenu.preuves_detectees);
  ajouterDifferenceTableau(differences, 'preuves_absentes', attendu.preuves_absentes, obtenu.preuves_absentes);
  ajouterDifferenceTableau(differences, 'contradictions', attendu.contradictions, obtenu.contradictions);

  return {
    conforme: differences.length === 0,
    statut: differences.length === 0 ? 'conforme' : 'regression',
    attendu,
    obtenu,
    differences,
    execute_le: new Date().toISOString(),
  };
};

const chargerIndexRegistre = async () => {
  try {
    const reponse = await fetch(URL_INDEX_REGISTRE_FLUX, { method: 'GET', cache: 'no-store' });
    if (!reponse.ok) throw new Error('HTTP ' + reponse.status);
    const index = await reponse.json();
    if (!index || !Array.isArray(index.fichiers)) return FICHIERS_REGISTRE_FALLBACK;
    return index.fichiers;
  } catch (erreur) {
    return FICHIERS_REGISTRE_FALLBACK;
  }
};

const chargerObjetRegistre = async (fichier) => {
  const chemin = '/registre-flux/' + fichier;
  const reponse = await fetch(chemin, { method: 'GET', cache: 'no-store' });
  if (!reponse.ok) throw new Error('Impossible de charger ' + chemin + ' (' + reponse.status + ')');
  const objet = await reponse.json();
  objet.__cheminRegistre = chemin;
  return objet;
};

const chargerGoldenCasesEmbarques = async () => {
  try {
    const reponse = await fetch(URL_GOLDEN_CASES, { method: 'GET', cache: 'no-store' });
    if (!reponse.ok) return [];
    const payload = await reponse.json();
    return payload && Array.isArray(payload.golden_cases) ? payload.golden_cases : [];
  } catch (erreur) {
    return [];
  }
};

const construireRegistre = (objets) => {
  const flux = objets
    .filter((objet) => objet.type_objet === 'flux')
    .sort((a, b) => libelleFlux(a).localeCompare(libelleFlux(b), 'fr'));
  const profils = objets
    .filter((objet) => objet.type_objet === 'profil_interpretation_flux')
    .sort((a, b) => libelleProfil(a).localeCompare(libelleProfil(b), 'fr'));
  const profilsParFlux = {};

  profils.forEach((profil) => {
    const fluxId = profil.identite && profil.identite.flux_id;
    if (fluxId) profilsParFlux[fluxId] = profil;
  });

  return {
    flux,
    profils,
    profilsParFlux,
    objets,
    genereA: new Date().toISOString(),
  };
};

export const libelleFlux = (flux) => (
  flux && flux.identite && (flux.identite.nom || flux.identite.id)
) || 'Flux sans nom';

export const libelleProfil = (profil) => (
  profil && profil.identite && (profil.identite.nom || profil.identite.id)
) || 'Profil sans nom';

export const idFlux = (flux) => flux && flux.identite && flux.identite.id;

export const idGoldenCase = (goldenCase) => goldenCase && goldenCase.identite && goldenCase.identite.id;

const preuveDetectee = (preuve, texte) => {
  const selecteur = preuve && preuve.selecteur ? preuve.selecteur : {};
  const operateur = selecteur.operateur;
  const valeur = selecteur.valeur;
  if (!texte || valeur === undefined || valeur === null) return false;

  if (operateur === 'contient') {
    return texte.toLowerCase().indexOf(String(valeur).toLowerCase()) >= 0;
  }
  if (operateur === 'pattern') {
    try {
      return new RegExp(String(valeur), 'i').test(texte);
    } catch (erreur) {
      return texte.toLowerCase().indexOf(String(valeur).toLowerCase()) >= 0;
    }
  }
  return false;
};

const reglesDeclencheesDepuisProfil = (profil, idsDetectes) => (
  ((profil && profil.regles_interpretation) || [])
    .filter((regle) => conditionRespectee(regle && regle.si, idsDetectes))
    .sort((a, b) => Number(a.priorite || 0) - Number(b.priorite || 0))
);

const conditionRespectee = (condition, idsDetectes) => {
  const toutes = (condition && condition.toutes) || [];
  const uneDes = (condition && condition.une_des) || [];
  const aucune = (condition && condition.aucune) || [];
  return toutes.every((id) => idsDetectes.has(id))
    && (uneDes.length === 0 || uneDes.some((id) => idsDetectes.has(id)))
    && aucune.every((id) => !idsDetectes.has(id));
};

const calculerContradictions = (profil, idsDetectes, detecteesAttendues, detecteesContradictoires) => {
  const parId = indexerPreuves(detecteesAttendues.concat(detecteesContradictoires));
  const contradictionsDeclarees = ((profil && profil.contradictions) || [])
    .filter((contradiction) => idsIncompatiblesDetectes(contradiction).every((id) => idsDetectes.has(id)))
    .map((contradiction) => ({
      id: contradiction.id,
      raison: contradiction.raison || 'Preuves incompatibles detectees.',
      preuves: idsIncompatiblesDetectes(contradiction).map((id) => parId[id]).filter(Boolean),
      etat_induit: contradiction.etat_induit || null,
      confiance: contradiction.confiance || 'forte',
      source: 'profil.contradictions',
    }));

  const contradictionsParRegles = ((profil && profil.regles_interpretation) || [])
    .filter((regle) => conditionPositiveRespectee(regle && regle.si, idsDetectes) && conditionAucuneViolee(regle && regle.si, idsDetectes))
    .map((regle) => {
      const idsBloquants = ((regle.si && regle.si.aucune) || []).filter((id) => idsDetectes.has(id));
      return {
        id: 'contradiction:' + nettoyerId(regle.id || 'regle'),
        raison: 'Une regle attend explicitement l absence de preuves pourtant detectees.',
        preuves: idsBloquants.map((id) => parId[id]).filter(Boolean),
        regle: regle.id,
        etat_induit: regle.alors && regle.alors.etat,
        confiance: regle.alors && regle.alors.confiance,
        source: 'profil.regles_interpretation',
      };
    });

  return dedupliquerParId(contradictionsDeclarees.concat(contradictionsParRegles));
};

const idsIncompatiblesDetectes = (contradiction) => (
  (contradiction && (contradiction.preuves_incompatibles || contradiction.preuves)) || []
);

const conditionPositiveRespectee = (condition, idsDetectes) => {
  const toutes = (condition && condition.toutes) || [];
  const uneDes = (condition && condition.une_des) || [];
  return toutes.every((id) => idsDetectes.has(id))
    && (uneDes.length === 0 || uneDes.some((id) => idsDetectes.has(id)));
};

const conditionAucuneViolee = (condition, idsDetectes) => (
  ((condition && condition.aucune) || []).some((id) => idsDetectes.has(id))
);

const diagnostiquerFluxLocal = ({
  profil,
  detecteesAttendues,
  detecteesContradictoires,
  absentes,
  contradictions,
  reglesDeclenchees,
  jalonsAtteints,
  jalonsNonAtteints,
}) => {
  const etatPrincipal = etatPrincipalDepuisRegles(profil, reglesDeclenchees);
  const preuvesRequisesManquantes = absentes.filter((preuve) => preuve.force_probante === 'requise' || preuve.force_probante === 'confirmante');
  const reglePrincipale = reglesDeclenchees[0] || null;

  if (contradictions.length > 0) {
    return construireDiagnostic({
      mission: 'non accomplie',
      raison: contradictions[0].raison,
      detecteesAttendues,
      detecteesContradictoires,
      absentes,
      contradictions,
      jalonsAtteints,
      jalonsNonAtteints,
      certitude: contradictions[0].confiance || 'forte',
      etat: contradictions[0].etat_induit || (etatPrincipal && etatPrincipal.id) || 'etat:contradiction',
      reglePrincipale,
    });
  }

  if (etatPrincipal && etatPrincipal.categorie === 'nominal' && preuvesRequisesManquantes.length === 0) {
    return construireDiagnostic({
      mission: 'accomplie',
      raison: reglePrincipale ? 'Regle nominale satisfaite par les preuves detectees.' : 'Preuves requises detectees.',
      detecteesAttendues,
      detecteesContradictoires,
      absentes,
      contradictions,
      jalonsAtteints,
      jalonsNonAtteints,
      certitude: (reglePrincipale && reglePrincipale.alors && reglePrincipale.alors.confiance) || 'moyenne',
      etat: etatPrincipal.id,
      reglePrincipale,
    });
  }

  if (detecteesContradictoires.length > 0 || etatIncidentOuBloquant(etatPrincipal)) {
    return construireDiagnostic({
      mission: 'non accomplie',
      raison: 'Preuve contradictoire ou etat bloquant detecte par le profil.',
      detecteesAttendues,
      detecteesContradictoires,
      absentes,
      contradictions,
      jalonsAtteints,
      jalonsNonAtteints,
      certitude: (reglePrincipale && reglePrincipale.alors && reglePrincipale.alors.confiance) || 'forte',
      etat: (etatPrincipal && etatPrincipal.id) || (detecteesContradictoires[0] && detecteesContradictoires[0].etat_induit) || 'etat:incident',
      reglePrincipale,
    });
  }

  if (preuvesRequisesManquantes.length > 0) {
    return construireDiagnostic({
      mission: detecteesAttendues.length > 0 ? 'indeterminee' : 'non accomplie',
      raison: 'Preuves requises ou confirmantes absentes.',
      detecteesAttendues,
      detecteesContradictoires,
      absentes,
      contradictions,
      jalonsAtteints,
      jalonsNonAtteints,
      certitude: (reglePrincipale && reglePrincipale.alors && reglePrincipale.alors.confiance) || 'moyenne',
      etat: (etatPrincipal && etatPrincipal.id) || 'etat:preuve_insuffisante',
      reglePrincipale,
    });
  }

  return construireDiagnostic({
    mission: 'indeterminee',
    raison: 'Aucune regle terminale du profil ne permet de conclure localement.',
    detecteesAttendues,
    detecteesContradictoires,
    absentes,
    contradictions,
    jalonsAtteints,
    jalonsNonAtteints,
    certitude: 'faible',
    etat: (etatPrincipal && etatPrincipal.id) || 'etat:inconnu',
    reglePrincipale,
  });
};

const construireDiagnostic = ({
  mission,
  raison,
  detecteesAttendues,
  detecteesContradictoires,
  absentes,
  contradictions,
  jalonsAtteints,
  jalonsNonAtteints,
  certitude,
  etat,
  reglePrincipale,
}) => ({
  mission,
  raison_principale: raison,
  preuves_positives: detecteesAttendues,
  preuves_contradictoires: detecteesContradictoires,
  preuves_manquantes: absentes,
  contradictions,
  jalons_atteints: jalonsAtteints,
  jalons_non_atteints: jalonsNonAtteints,
  niveau_certitude: certitude || 'faible',
  etat_flux: etat,
  regle_principale: reglePrincipale ? reglePrincipale.id : null,
});

const etatPrincipalDepuisRegles = (profil, reglesDeclenchees) => {
  const regle = reglesDeclenchees[0];
  const etatId = regle && regle.alors && regle.alors.etat;
  return ((profil && profil.etats) || []).find((etat) => etat.id === etatId) || null;
};

const etatIncidentOuBloquant = (etat) => etat && ['incident', 'bloque'].indexOf(etat.categorie) >= 0;

const indexerPreuves = (preuves) => (
  preuves.reduce((index, preuve) => {
    if (preuve && preuve.id) index[preuve.id] = preuve;
    return index;
  }, {})
);

const dedupliquerParId = (items) => {
  const vus = {};
  return items.filter((item) => {
    const id = item && item.id;
    if (!id || vus[id]) return false;
    vus[id] = true;
    return true;
  });
};

const nettoyerId = (id) => String(id).replace(/[^a-zA-Z0-9_.:-]/g, '_');

const jalonsConcernes = (preuves) => {
  const ids = {};
  preuves.forEach((preuve) => {
    if (preuve && preuve.jalon) ids[preuve.jalon] = true;
  });
  return Object.keys(ids).sort();
};

const resultatAttenduDepuisResultat = (resultat) => {
  const diagnostic = (resultat && resultat.diagnostic) || {};
  return {
    mission: diagnostic.mission || 'indeterminee',
    etat_flux: diagnostic.etat_flux || 'etat:inconnu',
    raison_principale: diagnostic.raison_principale || '',
    diagnostic,
    preuves_detectees: idsDepuisObjets(resultat && resultat.detectees),
    preuves_absentes: idsDepuisObjets(resultat && resultat.absentes),
    contradictions: idsDepuisObjets(resultat && resultat.contradictions),
    jalons_atteints: (resultat && resultat.jalons_atteints) || [],
    jalons_non_atteints: (resultat && resultat.jalons_non_atteints) || [],
  };
};

const idsDepuisObjets = (items) => (
  (items || [])
    .map((item) => item && item.id)
    .filter(Boolean)
    .sort()
);

const ajouterDifference = (differences, champ, attendu, obtenu) => {
  if (String(attendu || '') !== String(obtenu || '')) {
    differences.push({ champ, attendu: attendu || null, obtenu: obtenu || null });
  }
};

const ajouterDifferenceTableau = (differences, champ, attendu, obtenu) => {
  const attenduNormalise = normaliserTableau(attendu);
  const obtenuNormalise = normaliserTableau(obtenu);
  if (attenduNormalise.join('|') !== obtenuNormalise.join('|')) {
    differences.push({
      champ,
      attendu: attenduNormalise,
      obtenu: obtenuNormalise,
      manquants: attenduNormalise.filter((id) => obtenuNormalise.indexOf(id) < 0),
      ajoutes: obtenuNormalise.filter((id) => attenduNormalise.indexOf(id) < 0),
    });
  }
};

const normaliserTableau = (valeurs) => (
  (valeurs || [])
    .map((valeur) => String(valeur))
    .filter(Boolean)
    .sort()
);

const lireGoldenCasesLocaux = () => {
  if (typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const brut = window.localStorage.getItem(CLE_STOCKAGE_GOLDEN_CASES);
    const payload = brut ? JSON.parse(brut) : null;
    return payload && Array.isArray(payload.golden_cases) ? payload.golden_cases : [];
  } catch (erreur) {
    return [];
  }
};

const ecrireGoldenCasesLocaux = (goldenCases) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(CLE_STOCKAGE_GOLDEN_CASES, JSON.stringify({
    type_objet: 'bibliotheque_golden_cases_interpretation',
    version_schema: 'bibliotheque-golden-cases-interpretation.schema.v1',
    golden_cases: goldenCases,
    modifie_le: new Date().toISOString(),
  }, null, 2));
};

const fusionnerGoldenCases = (...collections) => {
  const parId = {};
  collections.forEach((collection) => {
    (collection || []).forEach((goldenCase) => {
      const id = idGoldenCase(goldenCase);
      if (id) parId[id] = goldenCase;
    });
  });
  return Object.keys(parId)
    .map((id) => parId[id])
    .sort((a, b) => dateGoldenCase(b).localeCompare(dateGoldenCase(a)));
};

const dateGoldenCase = (goldenCase) => (
  goldenCase && goldenCase.identite && (goldenCase.identite.modifie_le || goldenCase.identite.cree_le)
) || '';

const slugifier = (valeur) => String(valeur || 'cas')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  || 'cas';



// WEBPACK FOOTER //
// src/components/cockpit/laboratoireInterpretationService.js